import { useConnectionStore } from "@/modules/connection/store/connection";
import { useEventStore } from "@/modules/event/store/event";
import ArticleInterface from "@/modules/journal/interfaces/ArticleInterface";
import { useJournalStore } from "@/modules/journal/store/journal";
import { usePlaceStore } from "@/modules/place/store/place";
import { useRightStore } from "@/modules/rights/store/right";
import { Storage } from "@ionic/storage";
import { ref } from "vue";

export function useCachedFetch() {
  const storage = ref<Storage | null>(null);

  const getStorage = async () => {
    if (!storage.value) {
      storage.value = new Storage({ name: "__cache" });
      await storage.value.create();
    }
    return storage.value;
  };

  const cachedFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const connectionStore = useConnectionStore();

    // Only serve from cache when the connection is known to be down;
    // while the status is still unknown (app boot) go to the network,
    // the catch below falls back to cache if the request fails.
    if (
      (!init?.method || init?.method === "GET") &&
      connectionStore.connectionStatus?.connected === false
    ) {
      const cachedResponse = await (await getStorage()).get(input.toString());
      if (cachedResponse) {
        return new Response(JSON.stringify(cachedResponse), {
          headers: { "Content-Type": "application/json" },
        });
      }
    }

    try {
      const response = await fetch(input, init);

      // Only cache successful GET responses
      if (response.ok && (!init?.method || init?.method === "GET")) {
        const clonedResponse = response.clone();
        try {
          await (
            await getStorage()
          ).set(input.toString(), await clonedResponse.json());
        } catch (e) {
          // Non-JSON response, or other error during .json() or caching
          // console.warn("Could not cache non-JSON response or error during caching:", e);
        }
      }
      /* the original response can be resolved unmodified: */
      return response;
    } catch (error) {
      // If fetch fails (e.g., network error) and it's a GET request, try cache
      if (
        error instanceof TypeError &&
        (!init?.method || init?.method === "GET")
      ) {
        const cachedResponse = await (await getStorage()).get(input.toString());
        if (cachedResponse) {
          return new Response(JSON.stringify(cachedResponse), {
            headers: { "Content-Type": "application/json" },
          });
        }
      }
      // If not a TypeError, not a GET request, or no cache, re-throw the error
      throw error;
    }
  };

  const precache = async () => {
    const rateLimitInterval = 300; // 0.3 second
    const _delay = (ms: number) =>
      new Promise((resolve) => setTimeout(resolve, ms));
    let lastCallStartTime = 0;

    // Helper function to call store methods with rate limiting
    const callStoreMethodWithRateLimit = async <T>(
      method: () => Promise<T>
    ): Promise<T> => {
      const intendedStartTime =
        lastCallStartTime === 0
          ? Date.now()
          : lastCallStartTime + rateLimitInterval;
      const currentTime = Date.now();

      if (currentTime < intendedStartTime) {
        await _delay(intendedStartTime - currentTime);
      }

      lastCallStartTime = Date.now(); // Record the actual start time of the method call
      return method();
    };

    const placeStore = usePlaceStore();
    const rightStore = useRightStore();
    const journalStore = useJournalStore();
    const eventStore = useEventStore();

    // --- Places ---
    await callStoreMethodWithRateLimit(() => placeStore.getPlaceCategories());

    // Assuming placeStore.categories is populated by getPlaceCategories
    const categories = placeStore.categories; // Access after the call
    for (const category of categories) {
      await callStoreMethodWithRateLimit(() =>
        placeStore.fetchPlacesByCategory(category)
      );
    }

    // --- Rights ---
    await callStoreMethodWithRateLimit(() => rightStore.getRightsCategories());

    // --- Journal ---
    await callStoreMethodWithRateLimit(() => journalStore.getArticles());

    // Assuming journalStore.articles is populated by getArticles
    const articles = journalStore.articles; // Access after the call
    for (const article of articles) {
      await callStoreMethodWithRateLimit(
        () => journalStore.getArticle(article.id) as Promise<ArticleInterface>
      );
    }

    // --- Events ---
    await callStoreMethodWithRateLimit(() => eventStore.getEvents());
  };

  return {
    fetch: cachedFetch,
    precache,
  };
}
